'use client';

import { useEffect } from 'react';

import { apiClient } from '@/lib/api-client';

type JsonSchema = {
  type: 'object';
  properties?: Record<string, { type: string; enum?: string[]; minimum?: number; maximum?: number }>;
  required?: string[];
  additionalProperties?: boolean;
};

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: JsonSchema;
  execute: (input: Record<string, unknown>, options: { signal: AbortSignal }) => Promise<unknown>;
};

type ModelContext = {
  registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => void;
  unregisterTool?: (name: string) => void;
};

const emptySchema: JsonSchema = {
  type: 'object',
  additionalProperties: false,
};

const listSuggestionsSchema: JsonSchema = {
  type: 'object',
  properties: {
    track: { type: 'string', enum: ['Attention', 'Archive', 'Trash'] },
    limit: { type: 'number', minimum: 1, maximum: 50 },
    offset: { type: 'number', minimum: 0, maximum: 10000 },
  },
  additionalProperties: false,
};

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('The WebMCP request was cancelled.', 'AbortError');
  }
}

function getModelContext(): ModelContext | null {
  if (typeof navigator === 'undefined') return null;
  return navigator.modelContext ?? null;
}

export function WebMcpTools(): null {
  useEffect(() => {
    const modelContext = getModelContext();
    if (!modelContext) return undefined;

    const controller = new AbortController();
    const tools: WebMcpTool[] = [
      {
        name: 'purebox_browser_navigate',
        description: 'Open a PureBox page in this browser session without changing Gmail.',
        inputSchema: {
          type: 'object',
          properties: {
            path: { type: 'string', enum: ['/dashboard', '/cleanup', '/history', '/account'] },
          },
          required: ['path'],
          additionalProperties: false,
        },
        execute: async (input, options) => {
          throwIfAborted(options.signal);
          const path = input.path;
          if (typeof path !== 'string' || !path.startsWith('/')) {
            throw new TypeError('path must be a PureBox app path');
          }
          window.location.assign(path);
          return { navigatedTo: path };
        },
      },
      {
        name: 'purebox_browser_get_connection_status',
        description: 'Check whether PureBox and Gmail are connected in this browser session.',
        inputSchema: emptySchema,
        execute: async (_input, options) => {
          throwIfAborted(options.signal);
          const status = await apiClient.auth.getGmailOauthStatus();
          throwIfAborted(options.signal);
          return {
            ...status,
            accountUrl: '/account',
          };
        },
      },
      {
        name: 'purebox_browser_get_cleanup_status',
        description: 'Show whether the latest PureBox scan is complete and how much cleanup is waiting for review.',
        inputSchema: emptySchema,
        execute: async (_input, options) => {
          throwIfAborted(options.signal);
          const [scan, summary] = await Promise.all([
            apiClient.scan.current.query(),
            apiClient.account.getScanSummary(),
          ]);
          throwIfAborted(options.signal);
          return {
            scan,
            summary,
            reviewUrl: '/cleanup',
          };
        },
      },
      {
        name: 'purebox_browser_list_cleanup_suggestions',
        description: 'List current PureBox cleanup suggestions for review. Results are read-only and pinned to the selected filters.',
        inputSchema: listSuggestionsSchema,
        execute: async (input, options) => {
          throwIfAborted(options.signal);
          const track = input.track;
          const limit = typeof input.limit === 'number' ? input.limit : 20;
          const offset = typeof input.offset === 'number' ? input.offset : 0;
          const suggestions = await apiClient.classify.listSuggestions({
            status: 'pending',
            track: typeof track === 'string' ? track as 'Attention' | 'Archive' | 'Trash' : undefined,
            limit,
            offset,
          });
          throwIfAborted(options.signal);
          return {
            ...suggestions,
            reviewUrl: '/cleanup',
          };
        },
      },
    ];

    for (const tool of tools) {
      modelContext.registerTool(tool, { signal: controller.signal });
    }

    return () => {
      controller.abort();
      for (const tool of tools) {
        modelContext.unregisterTool?.(tool.name);
      }
    };
  }, []);

  return null;
}
